import { ActionContext, ActionTree } from 'vuex';
import LagerService from '@/modules/ewerp/service/LagerService';

import { RootState } from '@/modules/ewerp/store';
import { useEwerpCommonStore } from '@/modules/ewerp/store/ewerpCommonStore';

import { State } from './state';

import { Mutations, MutationTypes } from './mutations';

import { Listeartikel, LagerplatzResponse, Lagerplatzart } from '@/modules/ewerp/models/LagerModels';
import { Listeppsid } from '@/modules/ewerp/models/PpsModels';

export enum ActionTypes {
  GetArtikelImLager = 'GETARTIKELIMLAGER',
  UpdateGetArtikelImLager = 'UPDATEGETARTIKELIMLAGER',
  GetLagerzustand = 'GETLAGERZUSTAND',
  GetLagerpufferDaten = 'GETLAGERPUFFER',
  GetProduktionspufferDaten = 'GETPRODUKTIONSPUFFER',
  UpdateGetLagerzustand = 'UPDATEGETLAGERZUSTAND',
  GetLagerplatzartenDaten = 'GETLAGERPLATZARTEN',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetArtikelImLager](context: ActionArguments): Promise<void>;
  [ActionTypes.UpdateGetArtikelImLager](context: ActionArguments): Promise<void>;
  [ActionTypes.GetLagerzustand](context: ActionArguments): Promise<void>;
  [ActionTypes.GetLagerpufferDaten](context: ActionArguments): Promise<void>;
  [ActionTypes.GetProduktionspufferDaten](context: ActionArguments): Promise<void>;
  [ActionTypes.UpdateGetLagerzustand](context: ActionArguments): Promise<void>;
  [ActionTypes.GetLagerplatzartenDaten](context: ActionArguments): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [ActionTypes.GetArtikelImLager]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.lagerartikelLoading = true;
    commonStore.setStatus(status);

    return LagerService.getLagerArtikelBestand()
      .then((res) => {
        status.lagerartikelLoading = false;
        commonStore.setStatus(status);

        const result: Array<Listeartikel> = res.data;
        commit(MutationTypes.GetArtikelImLagerSuccess, result);
      })
      .catch((error) => {
        status.lagerartikelLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.lagerartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.lagerartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.lagerartikelErrorMsg = error;
        }

        sessionStorage.removeItem('bestandskontrolle');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UpdateGetArtikelImLager]({ commit }) {
    return LagerService.getLagerArtikelBestand()
      .then((res) => {
        const result: Array<Listeartikel> = res.data;
        commit(MutationTypes.GetArtikelImLagerSuccess, result);
      })
      .catch((error) => {
        const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.lagerartikelErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.lagerartikelErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.lagerartikelErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },

  // async getLagerplaetzeDaten({ commit, dispatch, rootGetters }) {
  //   commit('getLagerplaetzeDatenRequest')

  //   return LagerService.getLagerplaetze()
  //     .then(res => {
  //       commit('getLagerplaetzeDatenSuccess', res.data)
  //     })
  //     .catch(error => {
  //       commit('getLagerplaetzeDatenFailure', error.response.data.message);
  //     })
  // },

  async [ActionTypes.GetLagerzustand]({ commit, dispatch }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.lagerError = '';
    status.lagerLoading = true;
    commonStore.setStatus(status);

    return LagerService.getLagerplaetze()
      .then(async (res) => {
        const lagerplaetze: Array<LagerplatzResponse> = res.data;
        commit(MutationTypes.GetLagerplaetzeDatenSuccess, lagerplaetze);

        await dispatch(ActionTypes.GetArtikelImLager, undefined);
        await dispatch(ActionTypes.GetLagerpufferDaten, undefined);
        await dispatch(ActionTypes.GetProduktionspufferDaten, undefined);

        commit(MutationTypes.GetLagerzustandSuccess, undefined);
        status.lagerLoading = false;
        commonStore.setStatus(status);
      })
      .catch(() => {
        // commit('getLagerzustandFailure', error.response.data.message);
        status.lagerLoading = false;
        status.lagerError = 'Es ist ein Fehler aufgetreten.';
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UpdateGetLagerzustand]({ commit, dispatch }) {
    return LagerService.getLagerplaetze()
      .then((res) => {
        const lagerplaetze: Array<LagerplatzResponse> = res.data;
        commit(MutationTypes.GetLagerplaetzeDatenSuccess, lagerplaetze);

        dispatch(ActionTypes.UpdateGetArtikelImLager, undefined).then(() => {
          commit(MutationTypes.GetLagerzustandSuccess, undefined);
          commonStore.setStatus(status);
        });
      })
      .catch(() => {
        const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
        // commit('getLagerzustandFailure', error.response.data.message);
        status.lagerError = 'Es ist ein Fehler aufgetreten.';
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetLagerplatzartenDaten]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.lagerplatzartenLoading = '';
    status.lagerplatzartenLoading = true;
    commonStore.setStatus(status);

    return LagerService.getLagerplatzarten()
      .then((res) => {
        const result: Array<Lagerplatzart> = res.data;
        commit(MutationTypes.GetLagerplatzartenSuccess, result);
      })
      .catch((error) => {
        status.lagerplatzartenErrorMsg = error.toString();
        console.log(error);
      })
      .finally(() => {
        status.lagerplatzartenLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetLagerpufferDaten]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;

    return LagerService.getLagerpuffer()
      .then((res) => {
        const result: Array<Listeartikel> = res.data;
        commit(MutationTypes.GetLagerpufferDatenSuccess, result);
      })
      .catch((error) => {
        console.log(error);
        status.lagerError += error.toString();
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetProduktionspufferDaten]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;

    return LagerService.getProduktionspuffer()
      .then((res) => {
        const result: Array<Listeppsid> = res.data;
        commit(MutationTypes.GetProduktionspufferDatenSuccess, result);
      })
      .catch((error) => {
        console.log(error);
        status.lagerError += error.toString();
        commonStore.setStatus(status);
      });
  },
};
